// Contexto del onboarding guiado (tour de bienvenida).
//
// Orquesta el recorrido que se superpone a la app: decide qué pasos mostrar
// (filtrando por permisos del usuario), lleva el avance (paso actual / total),
// y expone las acciones (empezar, siguiente, anterior, saltar, terminar). El
// estado de "ya completado" es AUTORITATIVO en el backend (app_user) y se lee
// de `permissions.onboardingCompleted`; al completar/saltar o reiniciar se
// persiste con la API para que valga en todos los dispositivos del usuario.
//
// Arranque automático: la PRIMERA vez que un usuario con sesión tiene
// `onboardingCompleted === false` (p. ej. recién registrado), el tour se activa
// solo. El render del overlay vive en <OnboardingTour/>, montado por el Layout.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useApiMaybe } from '../api/ApiContext.js';
import { useAuth } from '../auth/AuthContext.js';
import { ONBOARDING_STEPS, type OnboardingStep } from './steps.js';

/** Estado y acciones del onboarding expuestas por el contexto. */
export interface OnboardingState {
  /** ¿El recorrido está activo (overlay visible)? */
  active: boolean;
  /** Índice del paso actual dentro de {@link steps}. */
  index: number;
  /** Pasos EFECTIVOS (ya filtrados por los permisos del usuario). */
  steps: ReadonlyArray<OnboardingStep>;
  /** Paso actual (o null si no hay recorrido activo). */
  current: OnboardingStep | null;
  /** Inicia (o reinicia) el recorrido desde el principio. */
  start: () => void;
  /** Avanza al siguiente paso; en el último, termina. */
  next: () => void;
  /** Retrocede al paso anterior (no pasa del primero). */
  prev: () => void;
  /** Salta el recorrido (lo marca como completado y lo cierra). */
  skip: () => void;
  /** Termina el recorrido (lo marca como completado y lo cierra). */
  finish: () => void;
  /** Reinicia el estado en backend y relanza el tour (control "reiniciar"). */
  restart: () => Promise<void>;
}

const OnboardingContext = createContext<OnboardingState | null>(null);

/**
 * Proveedor del onboarding. Debe montarse DENTRO de `ApiProvider` y `AuthProvider`
 * (necesita leer permisos y llamar a la API) y dentro del `BrowserRouter`.
 */
export function OnboardingProvider({ children }: { children: ReactNode }) {
  const { authenticated, permissions, hasPermission } = useAuth();
  // `useApiMaybe` (y no `useApi`) porque este provider envuelve toda la app
  // privada: si por cualquier motivo la API no está provista, degradamos a no-op
  // en vez de romper el árbol.
  const api = useApiMaybe();

  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);
  // Evita relanzar el auto-arranque más de una vez por sesión.
  const autoStarted = useRef(false);

  // Pasos efectivos: se omiten los que exigen una funcionalidad sin permiso de
  // lectura. Los pasos sin `feature` (bienvenida, menú, cierre) siempre entran.
  const steps = useMemo(
    () =>
      ONBOARDING_STEPS.filter((s) => {
        if (s.feature == null) return true;
        return hasPermission(s.feature, 'read');
      }),
    [hasPermission],
  );

  const start = useCallback(() => {
    setIndex(0);
    setActive(true);
  }, []);

  /** Persiste "completado" en backend (best-effort) y cierra el overlay. */
  const complete = useCallback(() => {
    setActive(false);
    setIndex(0);
    void api?.me.completeOnboarding().catch(() => {
      // Best-effort: si falla la red, el tour no se marca como completado y
      // podría reaparecer en el próximo acceso. No bloqueamos al usuario por eso.
    });
  }, [api]);

  const next = useCallback(() => {
    setIndex((i) => {
      if (i >= steps.length - 1) {
        complete();
        return i;
      }
      return i + 1;
    });
  }, [steps.length, complete]);

  const prev = useCallback(() => {
    setIndex((i) => (i > 0 ? i - 1 : 0));
  }, []);

  const skip = useCallback(() => complete(), [complete]);
  const finish = useCallback(() => complete(), [complete]);

  const restart = useCallback(async () => {
    // Reinicia el estado autoritativo en backend; si falla, igualmente abrimos
    // el tour localmente (el usuario lo pidió explícitamente).
    try {
      await api?.me.resetOnboarding();
    } catch {
      // Ignorado: relanzamos de todos modos.
    }
    start();
  }, [api, start]);

  // Arranque automático en el PRIMER acceso: con sesión y permisos cargados, si
  // el backend indica que el onboarding está pendiente, lo lanzamos una vez.
  useEffect(() => {
    if (autoStarted.current) return;
    if (!authenticated || !permissions) return;
    if (permissions.onboardingCompleted === false) {
      autoStarted.current = true;
      start();
    } else if (permissions.onboardingCompleted === true) {
      // Ya lo completó: marcamos el gate para no reconsiderarlo esta sesión.
      autoStarted.current = true;
    }
  }, [authenticated, permissions, start]);

  // Al cerrar sesión, resetea el gate para que un nuevo login reconsidere.
  useEffect(() => {
    if (!authenticated) autoStarted.current = false;
  }, [authenticated]);

  // Si el filtrado por permisos deja el índice fuera de rango, lo acotamos.
  useEffect(() => {
    if (active && index > steps.length - 1) {
      setIndex(Math.max(0, steps.length - 1));
    }
  }, [active, index, steps.length]);

  const value = useMemo<OnboardingState>(
    () => ({
      active,
      index,
      steps,
      current: active ? (steps[index] ?? null) : null,
      start,
      next,
      prev,
      skip,
      finish,
      restart,
    }),
    [active, index, steps, start, next, prev, skip, finish, restart],
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
}

/** Hook de acceso al onboarding. Lanza si se usa fuera del proveedor. */
export function useOnboarding(): OnboardingState {
  const ctx = useContext(OnboardingContext);
  if (!ctx) throw new Error('useOnboarding debe usarse dentro de <OnboardingProvider>');
  return ctx;
}

/** Variante que NO lanza (para componentes que pueden vivir sin el provider). */
export function useOnboardingMaybe(): OnboardingState | null {
  return useContext(OnboardingContext);
}
